// The two RECORD entities — the populations the map query itself scopes by,
// and the only two that have a quick-view detail endpoint behind them.
type EntityType = "project" | "lead"

// Every layer the map's legend can toggle. `subcontractor`/`appointment` are
// derived marker layers: the backend draws them on the very Project/Lead
// records they hang off (an assignment's project, an appointment's record),
// because neither population carries a geocoded location of its own.
type MapLayer = EntityType | "subcontractor" | "appointment"

const MAP_LAYERS: MapLayer[] = [
  "lead",
  "project",
  "subcontractor",
  "appointment"
]

// Which of the four layers is a record entity — the ones a marker click can
// open a quick view for.
const isRecordEntity = (layer: MapLayer): layer is EntityType =>
  layer === "project" || layer === "lead"

interface MapViewport {
  north: number
  south: number
  east: number
  west: number
  zoom: number
}

interface MapFeature {
  id: string
  entity_type: MapLayer
  latitude: number
  longitude: number
  precision: string
  status_id: number
  status_label: string
  title: string
  // Task 19 (ezbuild_back) — `reference` is the same L-000011/P-000010
  // display id the quick-view endpoints already use, always present.
  // `customer_name` is `null` whenever the record has no linked customer,
  // or the caller lacks `customer_view` — never an absent key, so a caller
  // can destructure it without an `in` check. Both ride this response
  // specifically so the Task 20 hover tooltip never needs a per-hover
  // fetch.
  reference: string
  customer_name: string | null
  // `address` — a one-line street address (house number, street, city, state,
  // zip), redacted to city/state only for a caller without exact-location
  // rights for this entity (same per-entity permission gate the coordinates
  // already go through). `null` only when even city/state are blank.
  address: string | null
  // A marker drawn on another record (an appointment) names that record, so
  // its panel can show and open it. Absent on every other marker, and on a
  // backend that predates them.
  record_id?: string | null
  record_type?: "lead" | "project" | null
  record_reference?: string | null
  record_title?: string | null
}

interface MapCluster {
  id: string
  latitude: number
  longitude: number
  count: number
  project_count: number
  lead_count: number
  expansion_zoom: number
}

interface FallbackCenter {
  latitude: number
  longitude: number
  zoom: number
}

// The "Look Around a Zip Code" filter's own resolved centroid — no `zoom`
// (unlike `FallbackCenter`), since the client always fits to the whole
// circle around this point (`map/fit.ts`'s `computeFit`/`nearRadiusFitBounds`)
// rather than the server dictating a zoom. Optional (not just nullable) so
// the many existing `MapFeaturesResponse` test fixtures that predate this
// field don't all need updating — every real caller already treats a
// missing key the same as `null` via `?.`/`??`.
interface NearCenter {
  latitude: number
  longitude: number
}

interface MapFeaturesResponse {
  // Client-side only, never on the wire: the layers this response was asked
  // for and the server refused, so the map could still answer for the rest.
  // Absent on every normal response — see `fetchMapFeatures`'s own retry.
  unavailable_layers?: MapLayer[]
  query_fingerprint: string
  total: number
  viewport_total: number
  unmapped: number
  truncated: boolean
  mode: "features" | "clusters"
  features: MapFeature[]
  clusters: MapCluster[]
  fallback_center: FallbackCenter | null
  near_center?: NearCenter | null
  server_timing_ms: number
  correlation_id: string
  project_total: number
  lead_total: number
  // The KPI row's own "Subcontractors"/"Appointments" tiles — same
  // both-entity/filter-aware contract as project_total/lead_total (see the
  // backend's own `MapFeaturesView.get` comment): never narrowed by the
  // `entities` layer toggle, but DOES reflect every other active filter,
  // so either tile updates as the user filters without needing a fetch of
  // its own. `appointment_total` counts only real (non-cancelled), FUTURE
  // appointments.
  subcontractor_total: number
  appointment_total: number
}

// `GET /global-map/favorites` — every project/lead the caller has starred,
// in the identical `MapFeature` row shape `/features` uses (backend's own
// `GlobalMapFavoritesView` reuses the same `build_features` helper), so the
// favorites panel can render with the same row component the Nearby tab
// already uses, with no second shape to learn.
interface FavoritesResponse {
  features: MapFeature[]
}

// Mirrors `ProjectQuickViewSerializer` (backend `apps/global_map/serializers.py`)
// exactly — nested `location`/`people`/`lifecycle`/`actions`, not a flat shape.
interface ProjectQuickViewLocation {
  precision: string
  freshness: string | null
  latitude: number | null
  longitude: number | null
  address: string | null
}

// Each key is present only when the viewer has the record and, for
// `customer`, the `user.customer_view` permission — the serializer omits a
// redacted/absent key rather than sending it as `null`. `subcontractors` is
// every assigned sub joined into one display string (never an empty one —
// the key itself is absent when nobody is assigned).
interface ProjectQuickViewPeople {
  project_manager?: string
  salesperson?: string
  // The record's primary sales manager under one name both entity types
  // answer to, so the panel reads one key instead of knowing that a project
  // calls its owner "salesperson".
  owner?: string
  customer?: string
  customer_phone?: string
  customer_email?: string
  subcontractors?: string
}

interface ProjectQuickViewLifecycle {
  start_date: string | null
  end_date: string | null
  created: string
  follow_up_date?: string
  appointment_date?: string
  appointment_time?: string
}

// The record's own photos, newest first — the panel's hero carousel.
// `count` is the real total, which can exceed `images.length` (the backend
// caps what it sends); both are `0`/`[]` on a `/list`/`/search` row, the same
// `include_overview_extras` gate every other per-record extra carries.
interface QuickViewMedia {
  images: { id: string; url: string | null; name: string | null }[]
  count: number
}

// The three figures the project panel shows, straight off
// `build_financial_snapshot` — the project page's own canonical calculation,
// on its `planned` basis (the contract's own numbers). `null` for a caller
// who may not see project money, never zero.
interface QuickViewFinancials {
  contract_value: number | null
  expenses: number | null
  margin: number | null
  margin_percent: number | null
}

interface ProjectQuickViewActions {
  open_project_url: string
}

interface ProjectQuickView {
  id: string
  entity_type: "project"
  reference: string
  title: string | null
  status_id: number
  status_label: string
  project_type: string | null
  location: ProjectQuickViewLocation
  people: ProjectQuickViewPeople
  lifecycle: ProjectQuickViewLifecycle
  updated_at: string
  actions: ProjectQuickViewActions
  // Both `null` on a `/list`/`/search` row (the backend only computes these
  // for the single-record detail fetch — see `include_overview_extras` in
  // `apps/global_map/serializers.py`, a real N+1 the list/search endpoints
  // hit once before that guard existed). `estimated_value` is additionally
  // `null` for a caller without `project_finance_view`.
  progress_percent: number | null
  estimated_value: number | null
  // `false` on a `/list`/`/search` row, same `include_overview_extras`
  // gate as `progress_percent`/`estimated_value` above — the star's real
  // state only ever matters on the single-record detail fetch.
  is_favorite: boolean
  media: QuickViewMedia
  financials: QuickViewFinancials | null
}

// Mirrors `LeadQuickViewSerializer` (backend `apps/global_map/serializers.py`)
// exactly — same nested `location`/`people`/`lifecycle`/`actions` shape as
// `ProjectQuickView`, adapted to a Lead's own fields.
interface LeadQuickView {
  id: string
  entity_type: "lead"
  reference: string
  title: string | null
  status_id: number
  status_label: string
  location: ProjectQuickView["location"]
  people: ProjectQuickViewPeople
  lifecycle: ProjectQuickViewLifecycle
  updated_at: string
  actions: { open_lead_url: string }
  is_favorite: boolean
  // A lead carries a project type too — same column on the shared
  // `core.Project` table, edited by the Lead workspace's own Scope tab.
  project_type: string | null
  summary: string | null
  media: QuickViewMedia
}

type EntityQuickView = ProjectQuickView | LeadQuickView

// Mirrors the List tab's own row shape (`GlobalMapListView.get`, backed by
// `ProjectQuickViewSerializer`/`LeadQuickViewSerializer` — confirmed against
// the real `ezbuild_back` serializers, not just the plan doc's sample) —
// distinct from `EntityQuickView` above (the map pin's quick-view card):
// `location.state`/`relevant_date`/`summary` are List-only additions.
type LocationState = "mapped" | "approximate" | "missing"

interface ListRowLocation {
  state: LocationState
  precision: string | null
  freshness: string | null
  latitude: number | null
  longitude: number | null
  address: string | null
}

// The real key set the backend can send here — confirmed against
// `ProjectQuickViewSerializer.get_people`/`LeadQuickViewSerializer.get_people`
// (`ezbuild_back/web_app/apps/global_map/serializers.py`): a Project omits a
// key entirely (never sends it as `null`) unless the corresponding person is
// assigned (`customer` additionally requires the viewer to hold
// `user.customer_view`); a Lead only ever sends `owner`. `Record<string,
// string>` previously stood in for this and let `to-display-row.ts` read a
// key (`salesperson`) that a Lead row never actually carries — a real
// key set here turns that class of mistake into a compile error instead of a
// silently blank column (final-review Critical #2).
interface ProjectOrLeadListRowPeople {
  project_manager?: string
  salesperson?: string
  owner?: string
  customer?: string
}

// Project and Lead rows share one flat shape (as they always have) — this is
// the pre-existing `ListRow` shape, renamed rather than widened, now that
// `ListRow` itself becomes a discriminated union over `entity_type` (below).
interface ProjectOrLeadListRow {
  id: string
  entity_type: "project" | "lead"
  reference: string
  title: string | null
  status_id: number
  status_label: string
  location: ListRowLocation
  people: ProjectOrLeadListRowPeople
  lifecycle: Record<string, string | null>
  relevant_date: string | null
  summary: string | null
  updated_at: string
  actions: Record<string, string>
}

// A Subcontractor row has no location/owner/date fields at all — it is not a
// geocoded record — so it is its own, unrelated shape rather than an
// all-optional bag sharing `ProjectOrLeadListRow`'s fields.
interface SubcontractorListRow {
  id: string
  entity_type: "subcontractor"
  name: string
  specialization_label: string | null
  status: string
  status_label: string
  active_project_count: number
}

// An Appointment row similarly has its own unrelated shape.
interface AppointmentListRow {
  id: string
  entity_type: "appointment"
  title: string | null
  related_record_title: string | null
  address: string | null
  status: string
  status_label: string
  assigned_user_name: string | null
  start_at: string
}

type ListRow = ProjectOrLeadListRow | SubcontractorListRow | AppointmentListRow

// Page-number pagination (2026-09-20 backend contract change, replacing
// cursor pagination): `page`/`page_size`/`total_pages` replace
// `next_cursor`/`previous_cursor`/`has_next`/`has_previous`. Every response
// now carries all four tab totals regardless of the active `list_entity`, so
// every sub-tab's own count is always up to date without a fetch of its own
// — mirrors `MapFeaturesResponse`'s own project_total/lead_total/
// subcontractor_total/appointment_total contract.
interface GlobalMapListResponse {
  query_fingerprint: string
  rows: ListRow[]
  total: number
  page: number
  page_size: number
  total_pages: number
  project_total: number
  lead_total: number
  subcontractor_total: number
  appointment_total: number
  correlation_id: string
}

// Task 12's normalized view-model — one shape `ListTab`'s 8-column table
// renders from, built by `helpers/to-display-row.ts`'s `toDisplayRow(row, t)`
// (one branch per `ListRow["entity_type"]`) so the table itself never
// branches on entity shape. Adapted from the plan's own sketch to this
// codebase's conventions in three ways: `owner` drops `initials` (the
// reused `ProjectsListPage` Avatar pattern falls back to a plain
// `UserOutlined` icon, never initials text); there is no `statusColor`
// field — `StatusPill` resolves color itself from `entityType`/`statusLabel`
// (calling the real `globalMapStatusStyle` helper for lead/project, its own
// small map for subcontractor/appointment) rather than threading a
// precomputed value through this row, which would risk a second place the
// Lead/Project palette could drift from `status-style.ts`; and
// `dateLabel`/`valueLabel` are real i18n keys' TRANSLATED text (the
// function takes a `TFunction`, mirroring `quick-view-fields.ts`'s own
// `toFieldRows(qv, t)` precedent) rather than hardcoded English, per this
// app's i18n convention.
interface GlobalMapListDisplayRow {
  id: string
  entityType: EntityType | "subcontractor" | "appointment"
  name: string
  secondaryText?: string
  locationPrimary?: string
  locationSecondary?: string
  locationState: LocationState
  statusLabel?: string
  owner?: { name: string; role?: string; avatarUrl?: string }
  datePrimary?: string
  dateLabel: string
  valuePrimary?: string
  valueLabel: string
}

type ListSortField = "relevant_date" | "name" | "status"
// "workforce" is the disabled tab's own key — Task 10's job to wire up, never
// fetched by this task's thunk/hook layer.
type ListEntityTab =
  | "all"
  | "project"
  | "lead"
  | "subcontractor"
  | "appointment"
  | "workforce"

export interface GlobalMapFilters {
  // Widened from single-id to multi-id per a later product decision — each
  // of the four relationship filters is now a checkbox dropdown, matching
  // `statusIds`/`projectTypeIds`'s own already-multi-select shape.
  salespersonIds: string[]
  projectManagerIds: string[]
  teamMemberIds: string[]
  subcontractorIds: string[]
  // Split from a single shared `statusIds` (real, reported bug fix):
  // `Lead.Status`/`Project.ProjectStatus` are two different backend enums
  // that collide numerically on purpose (e.g. Lead "New" == Project
  // "Planning" == 1), so one shared field made "check Planning" also check
  // "New" — two entirely separate fields so each only ever narrows its own
  // entity's own status column. See `ezbuild_back`'s
  // `GlobalMapFilters.project_status_ids`/`lead_status_ids` docstring for
  // the full story.
  projectStatusIds: number[]
  leadStatusIds: number[]
  projectTypeIds: string[]
  // Task 7 — restored, multi-select this time (the pre-removal version was a
  // single-value `region?: string` over a hardcoded 4-value set; List-tab's
  // `FacetMultiSelectFilter` needs an array like every other relationship
  // field, so this reintroduces it in that shape rather than the old one).
  region: string[]
  // `string | null` (not just `| undefined`) to match `PresetDateRangeFilter`'s
  // own emit contract — clearing writes an explicit `null`, same convention
  // as `ProjectFilters`' `created_after`/`created_before`.
  activePeriodStart?: string | null // ISO date, YYYY-MM-DD
  activePeriodEnd?: string | null
  // "Look Around a Zip Code" — both set together or neither (the trigger
  // beside the Lead/Project toggle asks for both before either reaches the
  // store). `nearRadiusMi` is one of `NEAR_RADIUS_STOPS_MI`
  // (global-map.constants.ts) — the backend 400s on any other value.
  // Entity-agnostic on purpose, unlike `projectStatusIds`/`leadStatusIds`:
  // there is no Lead/Project collision risk for a plain distance filter.
  nearZip?: string | null
  nearRadiusMi?: number | null
  // The "Appointment" filter's one option ("Future Appointments") — mirrors
  // the backend's own `has_upcoming_appointment` boolean (already used by
  // the KPI row's Appointments tile), now exposed as a real, explicit
  // filter pick rather than only a tile click. `false`/omitted means "no
  // narrowing", matching every other filter field's own default-empty
  // convention.
  hasUpcomingAppointment?: boolean
  // "History" — widens Map/List/Analytics to also include archived records
  // (mirrors the backend's own `include_history` boolean, same default-off/
  // additive-only shape as `hasUpcomingAppointment`): archived Projects
  // (Completed/Cancelled) always, archived Leads (the existing Inactive
  // Leads population) only for a caller the backend's own `lead_view_archive`
  // permission check allows — a caller without it still sees Projects widen,
  // silently, never an error. See `ezbuild_back`'s
  // `GlobalMapFilters.include_history` docstring for the full contract.
  includeHistory?: boolean
}

export const EMPTY_FILTERS: GlobalMapFilters = {
  salespersonIds: [],
  projectManagerIds: [],
  teamMemberIds: [],
  subcontractorIds: [],
  projectStatusIds: [],
  leadStatusIds: [],
  projectTypeIds: [],
  region: [],
  nearZip: null,
  nearRadiusMi: null,
  hasUpcomingAppointment: false,
  includeHistory: false
}

export interface FacetOption {
  value: string | number
  // Backend-confirmed nullable (`facets.py`'s `_full_name` returns `None`
  // when every name part is blank; `_simple_facet`'s label lookup can also
  // yield `""`) — every consumer must have a fallback, not just the one that
  // originally guarded it (`active-filter-summary.ts`'s `resolveOne`); this
  // type is what makes TypeScript enforce that at every other call site too.
  label: string | null
  count: number
  // Present only on the status facet (`_status_facet` in `ezbuild_back`'s
  // `facets.py`) — disambiguates which entity a status option belongs to,
  // since Lead and Project status ids collide numerically on purpose. Every
  // other facet leaves this `undefined`.
  entity?: "project" | "lead"
}

export interface GlobalMapFacets {
  status: FacetOption[]
  projectType: FacetOption[]
  region: FacetOption[]
  salesperson: FacetOption[]
  projectManager: FacetOption[]
  teamMember: FacetOption[]
  subcontractor: FacetOption[]
}

// The Analytics tab's one aggregate response — camelCase throughout,
// matching the backend's own JSON keys verbatim (`GlobalMapAnalyticsView`/
// `analytics.py`), so no snake_case-to-camelCase translation layer is
// needed here the way `GlobalMapFacetsResponse` needs one.
interface AnalyticsPeriod {
  startDate: string | null
  endDate: string | null
  previousStartDate: string | null
  previousEndDate: string | null
  timezone: string
}

// `value`/`previousValue`/`changePercent` are all `null` together whenever
// `available` is `false` (Workforce, At-Risk/SLA — no backing data exists
// anywhere in this deployment) — the UI must render its own "Data
// unavailable" state, never a fabricated 0 or "—" styled as a real number.
interface AnalyticsKpi {
  value: number | null
  previousValue: number | null
  changePercent: number | null
  changeLabel: "new" | null
  available: boolean
}

interface AnalyticsTotals {
  leads: AnalyticsKpi
  projects: AnalyticsKpi
  subcontractors: AnalyticsKpi
  appointments: AnalyticsKpi
  workforce: AnalyticsKpi
  atRiskSla: AnalyticsKpi
}

interface AnalyticsRecordsByTypeRow {
  entityType:
    | "leads"
    | "projects"
    | "subcontractors"
    | "appointments"
    | "workforce"
  color: string
  count: number | null
  percent: number | null
  available: boolean
}

interface AnalyticsRecordsByStatusRow {
  label: string
  count: number
  entity: "project" | "lead" | null
  value: number | null
}

interface AnalyticsTrendPoint {
  bucket: string
  counts: Record<string, number>
}

interface AnalyticsActivityTrend {
  bucketSize: "day" | "week" | "month"
  startDate: string
  endDate: string
  points: AnalyticsTrendPoint[]
  unavailableEntities: string[]
}

interface AnalyticsTopRegionRow {
  region: string
  total: number
  byEntityType: Record<string, number>
}

interface AnalyticsTopSalespersonRow {
  userId: string | null
  name: string | null
  total: number
  byEntityType: Record<string, number>
}

interface AnalyticsHeatmapPoint {
  latitude: number
  longitude: number
  entityType: EntityType
  weight: number
}

interface AnalyticsHeatmap {
  points: AnalyticsHeatmapPoint[]
  missingCoordinateCount: number
}

interface AnalyticsInsights {
  leadGrowth: {
    changePercent: number | null
    supportingRegions: string[]
    available: boolean
  }
  activeProjects: {
    count: number
    topRegion: string | null
    available: boolean
  }
  workforceUtilization: { value: number | null; available: boolean }
  slaAtRisk: {
    count: number | null
    leadCount: number | null
    projectCount: number | null
    available: boolean
  }
}

interface AnalyticsRecentActivityEvent {
  entityType: "project" | "lead"
  recordId: string
  recordTitle: string
  description: string
  timestamp: string
}

interface AnalyticsResponse {
  period: AnalyticsPeriod
  totals: AnalyticsTotals
  recordsByType: AnalyticsRecordsByTypeRow[]
  recordsByStatus: AnalyticsRecordsByStatusRow[]
  activityTrend: AnalyticsActivityTrend
  topRegions: AnalyticsTopRegionRow[]
  topSalespeople: AnalyticsTopSalespersonRow[]
  heatmap: AnalyticsHeatmap
  insights: AnalyticsInsights
  recentActivity: AnalyticsRecentActivityEvent[]
  serverTimingMs: number
  correlationId: string
}

// The Map tab's own "Recent Activity in View" card (below the map canvas) —
// a light, dedicated response (`GET /global-map/recent-activity`) rather
// than the full `AnalyticsResponse`, since the Map tab is this app's
// highest-traffic surface and only ever needs this one section. Lead and
// Project are each independently guaranteed up to 2 events — unlike
// `AnalyticsResponse.recentActivity`'s combined top-5, one entity type being
// more active can never crowd the other out of its own list (backend:
// `analytics.py::compute_recent_activity_by_entity`).
interface RecentActivityByEntityResponse {
  lead: AnalyticsRecentActivityEvent[]
  project: AnalyticsRecentActivityEvent[]
  serverTimingMs: number
  correlationId: string
}

interface GlobalMapCommittedQuery {
  layers: MapLayer[]
  filters: GlobalMapFilters
  // Free-text "search matching records" — kept OUT of GlobalMapCommittedQuery's
  // URL-synced shape per this plan's Global Constraints (no sensitive search
  // terms in the URL/telemetry); lives in GlobalMapState directly instead,
  // never round-tripped through useGlobalMapUrlSync's URL-writing effect.
}

interface GlobalMapState {
  committedQuery: GlobalMapCommittedQuery
  viewport: MapViewport | null
  data: MapFeaturesResponse | null
  lastFingerprint: string | null
  requestId: string | null
  status: "idle" | "loading" | "refreshing" | "error"
  error: { code: string; retryable: boolean } | null
  stale: boolean
  selectedFeatureId: string | null
  // Bumped by every `selectFeature` dispatch, including a reselect of the
  // SAME id (e.g. clicking a marker that's already the open selection) —
  // see `selectFeature`'s own comment for the real bug this exists to fix.
  selectionSeq: number
  quickView: {
    data: EntityQuickView | null
    status: "idle" | "loading" | "error"
    requestId: string | null
  }
  activeTab: "map" | "list" | "analytics"
  // Free-text search — see GlobalMapCommittedQuery's comment for why this
  // lives outside committedQuery rather than as one of its fields.
  searchTerm: string
  // Server-derived facet option lists for filter UIs — display data, not
  // committed query state, so it lives outside committedQuery like searchTerm.
  facets: GlobalMapFacets | null
  // Stale-response guard for `fetchFacets`, same convention as `requestId`
  // above for `fetchMapFeatures` — an older facets request resolving after a
  // newer one (e.g. two filter changes in quick succession) must not
  // overwrite the newer one's counts.
  facetsRequestId: string | null
  list: GlobalMapListState
  analytics: GlobalMapAnalyticsState
  recentActivityInView: GlobalMapRecentActivityState
}

// Mirrors `GlobalMapListState`'s own shape/naming — `status`/`error`/
// `requestId` follow the same stale-response-guard convention every other
// fetch in this slice already uses.
interface GlobalMapAnalyticsState {
  data: AnalyticsResponse | null
  status: "idle" | "loading" | "refreshing" | "error"
  error: { code: string; retryable: boolean } | null
  requestId: string | null
}

// Same stale-response-guard shape as `GlobalMapAnalyticsState`, for the Map
// tab's own light "Recent Activity in View" card.
interface GlobalMapRecentActivityState {
  data: RecentActivityByEntityResponse | null
  status: "idle" | "loading" | "refreshing" | "error"
  error: { code: string; retryable: boolean } | null
  requestId: string | null
}

// The List tab's own slice state — sub-tab selection, sort, direction, and
// the current page number (`currentPage`, set via `setListPage`) now that
// the backend has switched from cursor to page-number pagination.
// `pageSize` (Task 9, `setListPageSize`) is the client's own currently
// chosen page size — seeded at mount from the URL or the user's persisted
// `ui-preferences` choice (`useListFeatures`'s own mount effect), then kept
// in sync with `ListPaginationFooter`'s size changer.
interface GlobalMapListState {
  data: GlobalMapListResponse | null
  status: "idle" | "loading" | "refreshing" | "error"
  error: { code: string; retryable: boolean } | null
  requestId: string | null
  listEntity: ListEntityTab
  sort: ListSortField
  direction: "asc" | "desc"
  currentPage: number
  pageSize: number
  // Task 11 — the checked row ids for antd `Table`'s own `rowSelection`
  // (`selectedRowKeys`/`onChange`), read by Task 13's Export button. Cleared
  // on every action that already resets `currentPage` to 1 — a filters/
  // search/layers/sub-tab/sort/direction change is a new query shape, so a
  // selection made against the old rows would otherwise silently point at
  // whatever ids happen to occupy those positions in the new result set.
  selectedIds: string[]
}

export { MAP_LAYERS, isRecordEntity }

export type {
  EntityType,
  MapLayer,
  MapViewport,
  MapFeature,
  MapCluster,
  FallbackCenter,
  NearCenter,
  MapFeaturesResponse,
  FavoritesResponse,
  ProjectQuickViewLocation,
  ProjectQuickViewPeople,
  ProjectQuickViewLifecycle,
  ProjectQuickViewActions,
  QuickViewMedia,
  QuickViewFinancials,
  ProjectQuickView,
  LeadQuickView,
  EntityQuickView,
  LocationState,
  ListRowLocation,
  ProjectOrLeadListRow,
  SubcontractorListRow,
  AppointmentListRow,
  ListRow,
  GlobalMapListDisplayRow,
  GlobalMapListResponse,
  ListSortField,
  ListEntityTab,
  GlobalMapCommittedQuery,
  GlobalMapListState,
  GlobalMapAnalyticsState,
  GlobalMapState,
  AnalyticsResponse,
  AnalyticsPeriod,
  AnalyticsKpi,
  AnalyticsTotals,
  AnalyticsRecordsByTypeRow,
  AnalyticsRecordsByStatusRow,
  AnalyticsTrendPoint,
  AnalyticsActivityTrend,
  AnalyticsTopRegionRow,
  AnalyticsTopSalespersonRow,
  AnalyticsHeatmapPoint,
  AnalyticsHeatmap,
  AnalyticsInsights,
  AnalyticsRecentActivityEvent,
  RecentActivityByEntityResponse,
  GlobalMapRecentActivityState
}
